import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  EmployEngageLogo,
  GET_SHARED_RESPONSE,
  LoadingSpinner,
} from '@employ-engage/common-components';

type SharedResponse = {
  companyName: string;
  formTitle: string;
  respondent: string | null;
  submittedAt: string | null;
  expiresAt: string;
  answers: { number: number; question: string; answer: string }[];
};

/**
 * One form response, opened from the private link in a manager's text alert
 * (spec 001, PHN-52, PHN-53). Most managers have no account, so the link is
 * the key; the page is read-only and shows that response only.
 *
 * Reached only by a link, so it shows a way home beside everything else
 * (the 2026-10-30 rule), and anything that isn't a live link gets one plain
 * message and nothing about any response.
 */
export function SharedResponsePage() {
  const { token } = useParams();
  const { t, i18n } = useTranslation(['common']);
  const { data, loading } = useQuery(GET_SHARED_RESPONSE, {
    variables: { token },
    skip: !token,
    fetchPolicy: 'no-cache',
  });

  const date = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long' }).format(new Date(iso));

  const home = (
    <a
      href="/"
      className="inline-flex min-h-[44px] min-w-[44px] items-center rounded-md px-3 text-base font-semibold text-indigo-700 underline hover:bg-indigo-50"
    >
      {t('Go to Employ Engage')}
    </a>
  );

  if (loading) return <LoadingSpinner spinnerId="shared-response-loading" />;

  const response: SharedResponse | null = data?.sharedResponse ?? null;

  return (
    <main className="flex min-h-screen justify-center px-4 py-10 text-base">
      <div className="w-full max-w-2xl">
        <div className="mb-6 flex items-center justify-between gap-4">
          <EmployEngageLogo />
          {home}
        </div>

        {!response ? (
          <>
            <h1 className="text-2xl font-bold">{t("This link has expired or isn't valid.")}</h1>
            <p className="mt-3 text-gray-700">
              {t('Links to a response stop working 30 days after they are sent.')}
            </p>
          </>
        ) : (
          <>
            <h1 className="text-3xl font-bold">{response.formTitle}</h1>
            <p className="mt-2 text-gray-700">
              {response.respondent
                ? t('Response from {{name}}', { name: response.respondent })
                : t('Response from an anonymous employee')}
              {response.submittedAt &&
                ` · ${t('Submitted on {{date}}', { date: date(response.submittedAt) })}`}
            </p>

            <ol className="mt-6 space-y-5">
              {response.answers.map((item) => (
                <li key={item.number} className="rounded-lg border border-gray-200 p-4">
                  <p className="font-semibold">
                    {item.number}. {item.question}
                  </p>
                  {/* What people wrote, shown as written: text, never markup. */}
                  <p className="mt-2 whitespace-pre-wrap text-gray-800">{item.answer || '—'}</p>
                </li>
              ))}
            </ol>

            <p className="mt-8 text-gray-600">
              {t('This private link was texted to you by {{company}}. It stops working on {{date}}.', {
                company: response.companyName,
                date: date(response.expiresAt),
              })}
            </p>
          </>
        )}
      </div>
    </main>
  );
}

export default SharedResponsePage;
